import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import Button from "../../components/staff/Button";
import Image from "../../components/Atomos/Icons/Image";
import Trash from "../../components/Atomos/Icons/EditMode/Trash";
import Plus from "../../components/Atomos/Icons/EditMode/Plus";
import Update from "../../components/Atomos/Icons/Update";
import HelpCenterCreate from "../../components/Atomos/Icons/HelpCenterCreate";
import { Cancel } from "../../components/Atomos/Icons/Cancel";
import Close from "../../components/Atomos/Icons/Close";
import { getCachedHelpCenter, getHelpCenter, updateHelpCenter } from "../../Services/HelpCenter";
import type {
  HelpCenterAudience,
  IHelpCenter,
  IHelpCenterSection,
  IHelpCenterStep,
  IHelpCenterSubSection,
} from "../../interfaces/models/IHelpCenter";
import {
  audienceFromRouteParam,
  helpCenterCategoryPath,
  isHelpCenterNewSectionRouteSegment,
} from "./helpCenterRoutes";
import {
  createEmptyHelpCenterSection,
  findHelpCenterSection,
  sortByOrder,
  sortHelpCenterConfig,
} from "./helpCenterUtils";
import { toastError, toastPromise } from "../../utils/toast";
import { uploadHelpCenterFile } from "../../utils/uploadHelpCenterFile";
import HelpCenterEditUploadBox from "./HelpCenterEditUploadBox";
import HelpCenterDeleteGroupConfirmModal from "./HelpCenterDeleteGroupConfirmModal";
import {
  HelpCenterEditPdfPanel,
  HelpCenterEditTutorialPanel,
} from "./HelpCenterEditSectionMediaPanels";
import "./HelpCenter.css";

const STEP_DESCRIPTION_MAX = 4000;

function createId(prefix: string): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function createEmptyStep(sortOrder: number): IHelpCenterStep {
  return {
    id: createId("step"),
    sortOrder,
    description: "",
  };
}

function createEmptySubSection(sortOrder: number): IHelpCenterSubSection {
  return {
    id: createId("subsection"),
    title: "",
    sortOrder,
    isPublished: true,
    steps: [createEmptyStep(1)],
  };
}

function isBlankHelpCenterSection(section: IHelpCenterSection): boolean {
  if (section.title.trim()) {
    return false;
  }

  return section.subSections.every(
    (subSection) =>
      !subSection.title.trim() &&
      !subSection.tutorial?.mediaUrl &&
      !subSection.tutorial?.thumbnailUrl &&
      !subSection.pdfDownload?.mediaUrl &&
      !subSection.pdfDownload?.thumbnailUrl &&
      subSection.steps.every(
        (step) => !step.description.trim() && !step.imageUrl,
      ),
  );
}

function HelpCenterEditPageShell({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <div className="w-full pb-20">
      <div className="w-full bg-white help-center-shell">
        <div className="help-center-content">
          <div className="help-center-category-edit-container">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default function HelpCenterCategoryEditPage() {
  const navigate = useNavigate();
  const { audience: audienceParam, sectionId } = useParams();
  const audience = audienceFromRouteParam(audienceParam);
  const isCreatingNewSection = sectionId
    ? isHelpCenterNewSectionRouteSegment(sectionId)
    : false;
  const [config, setConfig] = useState<IHelpCenter | null>(null);
  const [section, setSection] = useState<IHelpCenterSection | null>(null);
  const [loading, setLoading] = useState(() => !getCachedHelpCenter());
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isNewSection, setIsNewSection] = useState(false);
  const [saveActionLabel, setSaveActionLabel] = useState<"Create" | "Update">(
    "Update",
  );
  const [isDeleteGroupModalOpen, setIsDeleteGroupModalOpen] = useState(false);
  const [isDeletingGroup, setIsDeletingGroup] = useState(false);

  const loadConfig = useCallback(async () => {
    if (!audience || !sectionId) {
      return;
    }

    try {
      setLoading(true);
      setLoadError(null);
      const data = sortHelpCenterConfig(await getHelpCenter());
      let matched = isCreatingNewSection
        ? createEmptyHelpCenterSection(data[audience].sections.length + 1)
        : findHelpCenterSection(data, audience, sectionId);

      if (!matched) {
        setLoadError("This Help Center topic could not be found.");
        setConfig(null);
        setSection(null);
        setIsNewSection(false);
        return;
      }

      const normalizedSection: IHelpCenterSection = {
        ...matched,
        subSections:
          matched.subSections.length > 0
            ? sortByOrder(matched.subSections).map((subSection) => ({
                ...subSection,
                steps: sortByOrder(subSection.steps),
              }))
            : [createEmptySubSection(1)],
      };

      setConfig(data);
      setSection(normalizedSection);
      setIsNewSection(isCreatingNewSection);
      setSaveActionLabel(
        isCreatingNewSection || isBlankHelpCenterSection(normalizedSection)
          ? "Create"
          : "Update",
      );
    } catch (error) {
      console.error("Failed to load help center category edit page", error);
      setLoadError("Failed to load Help Center topic.");
    } finally {
      setLoading(false);
    }
  }, [audience, isCreatingNewSection, sectionId]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  const updateSection = (
    updater: (current: IHelpCenterSection) => IHelpCenterSection,
  ) => {
    setSection((current) => (current ? updater(current) : current));
  };

  const updateSubSection = (
    subSectionId: string,
    updater: (current: IHelpCenterSubSection) => IHelpCenterSubSection,
  ) => {
    updateSection((current) => ({
      ...current,
      subSections: current.subSections.map((subSection) =>
        subSection.id === subSectionId ? updater(subSection) : subSection,
      ),
    }));
  };

  const handleUpload = async (
    file: File,
    onUploaded: (url: string) => void,
  ) => {
    if (!section?.id) {
      return;
    }
    const url = await uploadHelpCenterFile(file, section.id);
    if (url) {
      onUploaded(url);
      return;
    }
    toastError("Failed to upload file.");
  };

  const handleSave = async () => {
    if (!config || !section || !audience) {
      return;
    }

    const audienceSections = config[audience].sections;
    const nextConfig: IHelpCenter = {
      ...config,
      [audience]: {
        ...config[audience],
        sections: isNewSection
          ? [...audienceSections, section]
          : audienceSections.map((item) =>
              item.id === section.id ? section : item,
            ),
      },
    };

    try {
      await toastPromise(updateHelpCenter(sortHelpCenterConfig(nextConfig)), {
        pending: isNewSection
          ? "Creating Help Center topic..."
          : "Saving Help Center topic...",
        success: isNewSection
          ? "Help Center topic created successfully."
          : "Help Center topic saved successfully.",
      });
      setIsNewSection(false);
      navigate(helpCenterCategoryPath(audience, section.id));
    } catch (error) {
      console.error("Failed to save help center category", error);
      toastError("Failed to save Help Center topic.");
    }
  };

  const handleDeleteGroup = async () => {
    if (!config || !section || !audience || isNewSection) {
      return;
    }

    const nextConfig: IHelpCenter = {
      ...config,
      [audience]: {
        ...config[audience],
        sections: config[audience].sections
          .filter((item) => item.id !== section.id)
          .map((item, index) => ({
            ...item,
            sortOrder: index + 1,
          })),
      },
    };

    try {
      setIsDeletingGroup(true);
      await toastPromise(updateHelpCenter(sortHelpCenterConfig(nextConfig)), {
        pending: "Deleting group...",
        success: "Group deleted successfully.",
      });
      setIsDeleteGroupModalOpen(false);
      navigate("/help-center");
    } catch (error) {
      console.error("Failed to delete help center group", error);
      toastError("Failed to delete group.");
    } finally {
      setIsDeletingGroup(false);
    }
  };

  if (!audience || !sectionId) {
    return <Navigate to="/help-center" replace />;
  }

  if (loading) {
    return <HelpCenterEditPageShell>{null}</HelpCenterEditPageShell>;
  }

  if (loadError || !section) {
    return (
      <HelpCenterEditPageShell>
        <p className="help-center-error-banner">
          {loadError ?? "Help Center topic is unavailable."}
        </p>
        <Link to="/help-center">
          <Button
            text="Back to Help Center"
            color="primary"
            variant="filled"
            size="fixed"
            onClick={() => undefined}
          />
        </Link>
      </HelpCenterEditPageShell>
    );
  }

  const orderedSubSections = sortByOrder(section.subSections);

  return (
    <HelpCenterEditPageShell>
      <div className="help-center-category-edit-container help-center-fade-in">
        <div className="help-center-edit-group-block">
          <label className="help-center-edit-field help-center-edit-title-field">
            <span className="help-center-edit-title-label">Category Title</span>
            <div className="help-center-edit-title-row">
              <div className="help-center-edit-input-clearable help-center-edit-title-input-wrap">
                <input
                  type="text"
                  className="help-center-edit-title-input"
                  placeholder="Write a Title for this category"
                  value={section.title}
                  onChange={(event) =>
                    updateSection((current) => ({
                      ...current,
                      title: event.target.value,
                    }))
                  }
                />
                {section.title ? (
                  <button
                    type="button"
                    className="help-center-edit-input-clear help-center-edit-title-input-clear"
                    aria-label="Clear category title"
                    onClick={() =>
                      updateSection((current) => ({
                        ...current,
                        title: "",
                      }))
                    }
                  >
                    <Close className="h-5 w-5" />
                  </button>
                ) : null}
              </div>
              {!isNewSection ? (
                <button
                  type="button"
                  className="help-center-edit-delete-category-btn"
                  onClick={() => setIsDeleteGroupModalOpen(true)}
                >
                  <span className="help-center-edit-delete-category-btn-text">
                    Delete Category
                  </span>
                  <span className="help-center-edit-delete-category-btn-icon">
                    <Trash />
                  </span>
                </button>
              ) : null}
            </div>
          </label>
        </div>

        <div className="help-center-edit-sections-block">
          {orderedSubSections.map((subSection) => {
            const orderedSteps = sortByOrder(subSection.steps);

            return (
              <div
                key={subSection.id}
                className="help-center-edit-section-block"
              >
                <div className="help-center-edit-section-layout">
                  <div className="help-center-edit-section-main">
                    <label className="help-center-edit-field help-center-edit-title-field">
                      <span className="help-center-edit-title-label">
                        Section Title
                      </span>
                      <div className="help-center-edit-title-row">
                        <div className="help-center-edit-input-clearable help-center-edit-title-input-wrap">
                          <input
                            type="text"
                            className="help-center-edit-title-input"
                            placeholder="Write a Title for this section"
                            value={subSection.title}
                            onChange={(event) =>
                              updateSubSection(subSection.id, (current) => ({
                                ...current,
                                title: event.target.value,
                              }))
                            }
                          />
                        </div>
                        {orderedSubSections.length > 1 ? (
                          <button
                            type="button"
                            className="help-center-edit-delete-section-btn"
                            onClick={() =>
                              updateSection((current) => ({
                                ...current,
                                subSections: current.subSections
                                  .filter((item) => item.id !== subSection.id)
                                  .map((item, index) => ({
                                    ...item,
                                    sortOrder: index + 1,
                                  })),
                              }))
                            }
                          >
                            <span className="help-center-edit-delete-section-btn-text">
                              Delete Section
                            </span>
                            <span className="help-center-edit-delete-section-btn-icon">
                              <Trash />
                            </span>
                          </button>
                        ) : null}
                      </div>
                    </label>

                    <div className="help-center-edit-steps-layout">
                      {orderedSteps.map((step, stepIndex) => (
                        <div
                          key={step.id}
                          className="help-center-edit-step-pair-row"
                        >
                          <div className="help-center-edit-step-card">
                            <div className="help-center-edit-step-card-header">
                              <span className="help-center-edit-step-card-title">
                                Step {stepIndex + 1}
                              </span>
                              {orderedSteps.length > 1 ? (
                                <button
                                  type="button"
                                  className="help-center-edit-delete-step-btn"
                                  onClick={() =>
                                    updateSubSection(subSection.id, (current) => ({
                                      ...current,
                                      steps: current.steps
                                        .filter((item) => item.id !== step.id)
                                        .map((item, index) => ({
                                          ...item,
                                          sortOrder: index + 1,
                                        })),
                                    }))
                                  }
                                >
                                  <span className="help-center-edit-delete-step-btn-text">
                                    Delete Step
                                  </span>
                                  <span className="help-center-edit-delete-step-btn-icon">
                                    <Trash />
                                  </span>
                                </button>
                              ) : null}
                            </div>

                            <div className="help-center-edit-step-card-body">
                              <div className="help-center-edit-step-row">
                                <span className="help-center-edit-field-label help-center-edit-step-row-label">
                                  Description
                                </span>
                                <span className="help-center-edit-field-label help-center-edit-step-row-label">
                                  Upload Image
                                </span>
                                <div className="help-center-edit-step-description-input">
                                  <textarea
                                    className="help-center-edit-textarea help-center-edit-step-textarea"
                                    placeholder="Write a description for this section..."
                                    value={step.description}
                                    maxLength={STEP_DESCRIPTION_MAX}
                                    onChange={(event) =>
                                      updateSubSection(subSection.id, (current) => ({
                                        ...current,
                                        steps: current.steps.map((item) =>
                                          item.id === step.id
                                            ? {
                                                ...item,
                                                description: event.target.value,
                                              }
                                            : item,
                                        ),
                                      }))
                                    }
                                  />
                                </div>
                                <div className="help-center-edit-step-upload-input">
                                  <HelpCenterEditUploadBox
                                    label="Image"
                                    icon={<Image />}
                                    accept="image/*"
                                    previewUrl={step.imageUrl}
                                    showHint={false}
                                    dropzoneClassName="help-center-edit-upload-dropzone-step"
                                    onFileSelected={(file) =>
                                      handleUpload(file, (url) =>
                                        updateSubSection(subSection.id, (current) => ({
                                          ...current,
                                          steps: current.steps.map((item) =>
                                            item.id === step.id
                                              ? { ...item, imageUrl: url }
                                              : item,
                                          ),
                                        })),
                                      )
                                    }
                                    onClear={() =>
                                      updateSubSection(subSection.id, (current) => ({
                                        ...current,
                                        steps: current.steps.map((item) =>
                                          item.id === step.id
                                            ? { ...item, imageUrl: undefined }
                                            : item,
                                        ),
                                      }))
                                    }
                                  />
                                </div>
                                <span className="help-center-edit-char-count help-center-edit-step-char-count">
                                  {step.description.length} / {STEP_DESCRIPTION_MAX}
                                </span>
                              </div>
                            </div>
                          </div>

                          {stepIndex === 0 ? (
                            <HelpCenterEditTutorialPanel
                              subSection={subSection}
                              onUpload={handleUpload}
                              onUpdate={(updater) =>
                                updateSubSection(subSection.id, updater)
                              }
                            />
                          ) : null}

                          {stepIndex === 1 ? (
                            <HelpCenterEditPdfPanel
                              subSection={subSection}
                              onUpload={handleUpload}
                              onUpdate={(updater) =>
                                updateSubSection(subSection.id, updater)
                              }
                            />
                          ) : null}
                        </div>
                      ))}

                      {orderedSteps.length === 1 ? (
                        <div className="help-center-edit-step-pair-row">
                          <div
                            className="help-center-edit-step-pair-placeholder"
                            aria-hidden="true"
                          >
                            <div className="help-center-edit-step-card help-center-edit-step-pair-placeholder-card">
                              <div className="help-center-edit-step-card-header">
                                <span className="help-center-edit-step-card-title">
                                  Step 2
                                </span>
                              </div>
                              <div className="help-center-edit-step-card-body">
                                <div className="help-center-edit-step-row">
                                  <span className="help-center-edit-field-label help-center-edit-step-row-label">
                                    Description
                                  </span>
                                  <span className="help-center-edit-field-label help-center-edit-step-row-label">
                                    Upload Image
                                  </span>
                                  <div className="help-center-edit-step-description-input" />
                                  <div className="help-center-edit-step-upload-input" />
                                  <span className="help-center-edit-char-count help-center-edit-step-char-count">
                                    &nbsp;
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <HelpCenterEditPdfPanel
                            subSection={subSection}
                            onUpload={handleUpload}
                            onUpdate={(updater) =>
                              updateSubSection(subSection.id, updater)
                            }
                          />
                        </div>
                      ) : null}

                      <div className="help-center-edit-add-step-row">
                        <Button
                          text="Add Step"
                          icon={<Plus />}
                          color="secondary"
                          variant="filled"
                          size="fixed"
                          className="!gap-[12px]"
                          textClassName="font-normal"
                          onClick={() =>
                            updateSubSection(subSection.id, (current) => ({
                              ...current,
                              steps: [
                                ...sortByOrder(current.steps),
                                createEmptyStep(current.steps.length + 1),
                              ],
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="help-center-edit-add-section-block">
          <button
            type="button"
            className="help-center-edit-add-section-button"
            onClick={() => {
              const next = createEmptySubSection(section.subSections.length + 1);
              updateSection((current) => ({
                ...current,
                subSections: [...current.subSections, next],
              }));
            }}
          >
            <span className="help-center-edit-add-section-button-text">
              Add New Section
            </span>
            <span className="help-center-edit-add-section-button-icon" aria-hidden="true">
              <Plus />
            </span>
          </button>
        </div>

        <div className="help-center-edit-actions">
            <Button
              text="Cancel"
              color="white"
              variant="outlined"
              size="fixed"
              icon={<Cancel />}
              iconPosition="right"
              className="help-center-edit-cancel-button"
              onClick={() => {
                if (isNewSection) {
                  navigate("/help-center");
                  return;
                }

                navigate(helpCenterCategoryPath(audience, section.id));
              }}
            />
            <Button
              text={saveActionLabel}
              color="secondary"
              variant="filled"
              size="fixed"
              icon={
                saveActionLabel === "Create" ? (
                  <HelpCenterCreate className="help-center-edit-create-icon" />
                ) : (
                  <Update className="h-5 w-5" />
                )
              }
              iconPosition="right"
              className="help-center-edit-save-button"
              onClick={() => void handleSave()}
            />
        </div>
      </div>
      <HelpCenterDeleteGroupConfirmModal
        isOpen={isDeleteGroupModalOpen}
        isSubmitting={isDeletingGroup}
        onCancel={() => setIsDeleteGroupModalOpen(false)}
        onConfirm={() => void handleDeleteGroup()}
      />
    </HelpCenterEditPageShell>
  );
}
